'use client';

import ControlledTabs from '@umodeapporg/ui/components/Form/ControlledTabs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';

import { formatDiaLongo } from '@/app/relatorios/helpers/formatRelatorios';
import { VisaoExecutivaPanel } from '@/app/visao-executiva/VisaoExecutivaPanel';
import { HomeCopilotoBox } from '@/components/home/HomeCopilotoBox';
import { HomeHojePanel } from '@/components/home/HomeHojePanel';
import { HOME_TAB_VALUES, TAB_EXECUTIVA, TAB_HOJE, TAB_PADRAO } from '@/components/home/homeTabs';
import PageTitle from '@/components/layout/PageTitle';
import { useActiveTabParam } from '@/domain/common/hooks/useActiveTabParam';
import { hojeNaFabricaISO } from '@/domain/configuracao/helpers/hojeNaFabricaISO';
import { useFila } from '@/domain/fila/hooks/useFila';
import { PERMISSION, TPermissionId } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { authStore } from '@/infrastructure/store/authStore';

/** The Copiloto box is taller than the title, so the header pins it to the TOP. */
const SLOT_NO_TOPO = 'start';

/** The Fila opens for whoever follows it or the raw material. */
const FILA_VIEW_PERMISSIONS: ReadonlyArray<TPermissionId> = [PERMISSION.FILA_VIEW, PERMISSION.MATERIAIS_VIEW];

/** The executive view reads the whole portfolio, the factory grid and the delivered punctuality. */
const EXECUTIVA_PERMISSIONS: ReadonlyArray<TPermissionId> = [
  PERMISSION.CARTEIRA_VIEW,
  PERMISSION.MAPA_VIEW,
  PERMISSION.RELATORIOS_VIEW,
];

/**
 * Início, em duas abas: "Hoje" é o guia do dia e "Visão executiva" é a leitura de quem
 * decide, que antes tinha rota própria. As duas respondem perguntas diferentes sobre a
 * mesma fábrica, e juntá-las tira um item do menu sem tirar nada da tela.
 *
 * A aba viaja na barra de endereço, então recarregar (ou abrir um link mandado por alguém)
 * cai na mesma aba. A fila é carregada AQUI porque o Copiloto mora no título, que as duas
 * abas dividem: deixá-la no painel "Hoje" apagaria o Copiloto na outra aba.
 */
export default function HomePage() {
  const { t } = useTranslation('common');
  const { can, canAny } = usePermissions();
  // Each block reads its own area: a role without it neither loads it nor sees the block.
  const veFila = canAny(FILA_VIEW_PERMISSIONS);
  const veExecutiva = EXECUTIVA_PERMISSIONS.every((id) => can(id));
  const abasPermitidas = useMemo(
    () => HOME_TAB_VALUES.filter((aba) => aba !== TAB_EXECUTIVA || veExecutiva),
    [veExecutiva],
  );
  const { activeTab, onTabChange } = useActiveTabParam(abasPermitidas, TAB_PADRAO);
  const { fila, loadFila } = useFila();
  /** Who is reading: the name of the unified login session, the same one the header menu shows. */
  const nomeLogado = useStore(authStore, (s) => s.user?.name ?? '');

  useEffect(() => {
    if (!veFila) {
      return;
    }
    const run = async (): Promise<void> => {
      await loadFila();
    };
    run();
  }, [loadFila, veFila]);

  /** The greeting needs a name: while the session has not brought one, the screen keeps its own. */
  const saudacao = nomeLogado === '' ? t('home.title') : t('home.saudacao', { nome: nomeLogado });

  /** The day the FACTORY is working, the same one the executive tab stamps on its header. */
  const linhaDoDia = t('home.producaoNoDia', { produto: t('produto.nome'), data: formatDiaLongo(hojeNaFabricaISO()) });

  const tabs = useMemo(
    () => [
      { value: TAB_HOJE, label: t('home.tabs.hoje') },
      ...(veExecutiva ? [{ value: TAB_EXECUTIVA, label: t('home.tabs.executiva') }] : []),
    ],
    [t, veExecutiva],
  );

  const dataTabs = useMemo(
    () => [
      { key: TAB_HOJE, element: <HomeHojePanel /> },
      ...(veExecutiva ? [{ key: TAB_EXECUTIVA, element: <VisaoExecutivaPanel /> }] : []),
    ],
    [veExecutiva],
  );

  return (
    <div className="flex min-h-full w-full flex-col gap-y-5 pb-8">
      {/* O Copiloto na MESMA linha do titulo: a pergunta do dia e a primeira coisa a
          alcance da mao, e o rodape deixa de carregar uma faixa so para isso. */}
      <PageTitle
        title={saudacao}
        headline={linhaDoDia}
        slot={<HomeCopilotoBox itens={fila?.itens ?? []} />}
        slotAlign={SLOT_NO_TOPO}
        showDivider={false}
      />
      <ControlledTabs
        initialActiveTab={TAB_PADRAO}
        activeTab={activeTab}
        onTabChange={onTabChange}
        tabs={tabs}
        dataTabs={dataTabs}
        tabsBodyClassName="pt-5"
        syncWithUrl={false}
      />
    </div>
  );
}
